import React, { useMemo, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import type { GcpServiceAccountForList } from '@/lib/queries/gcpServiceAccount';

import { useChat } from '../components/providers/ChatProvider';

export const useGcpSyncNotification = ({
  gcpServiceAccounts,
}: {
  gcpServiceAccounts: GcpServiceAccountForList[] | undefined;
}): void => {
  const { toast, dismiss } = useToast();
  const { setPrompt } = useChat();
  const previousGcpStatuses = useRef<Record<string, string>>({});

  useMemo(() => {
    const transitionedAccounts: string[] = [];

    gcpServiceAccounts?.forEach(account => {
      const prevStatus = previousGcpStatuses.current[account.id];
      if (
        prevStatus !== undefined &&
        prevStatus === 'inactive' &&
        account.status === 'active'
      ) {
        transitionedAccounts.push(account.name);
      }
      previousGcpStatuses.current[account.id] = account.status;
    });

    if (transitionedAccounts.length > 0) {
      toast({
        title: 'integrations.gcp.syncComplete',
        variant: 'default',
        description: 'integrations.gcp.tryIt',
        descriptionValues: {
          button: (parts: React.ReactNode) => (
            <>
              <br />
              <Button
                variant="main"
                className="mt-2"
                onClick={() => {
                  dismiss();
                  setPrompt(
                    'List all GCP projects and their associated resources across my GCP accounts',
                  );
                }}
              >
                {parts}
              </Button>
            </>
          ),
        },
      });
    }
  }, [gcpServiceAccounts, toast, dismiss, setPrompt]);
};
